import { useRef, useState } from "react";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { ProgressIcon } from "../../components/icons";
import PlaceholderFade from "../../components/PlaceholderFade";
import SectionHeader from "../../components/SectionHeader";
import {
  useAchievements,
  useMarkAchievementsSeen,
} from "../../hooks/useCustomQuery";
import AchievementRow from "./AchievementRow";

/**
 * Dashboard "Your progress": everything measured against a target, filtered
 * by the pills — today the per-type badges, later user-created goals (which
 * would lead). Getting started shares the query but sits at the top of the
 * page instead (DashboardGettingStarted). Rows come fully formed from
 * the backend, so this only lays them out. Renders nothing with no rows.
 *
 * NEW dots clear once seen: a NEW row that comes on screen is reported to the
 * server, but keeps its dot for the rest of this visit — later refetches say
 * it's seen, and it shouldn't vanish while you're looking at it.
 */
export default function DashboardProgress({ contentType }) {
  const { data, isLoading, isError, isPlaceholderData } =
    useAchievements(contentType);
  const rows = data?.filter((row) => row.kind !== "checklist");
  const markSeen = useMarkAchievementsSeen();
  const [newThisVisit, setNewThisVisit] = useState(() => new Set());
  const reportedRef = useRef(new Set()); // reported this visit, so once each

  const handleSeen = (key) => {
    if (reportedRef.current.has(key)) return;
    reportedRef.current.add(key);
    setNewThisVisit((keys) => new Set(keys).add(key));
    markSeen.mutate([key]);
  };

  if (!isLoading && !isError && !rows?.length) return null;

  return (
    <Box>
      <SectionHeader
        title="Your progress"
        icon={<ProgressIcon color="primary" />}
      />
      {isError ? (
        <Typography variant="body2" color="text.secondary" py={1}>
          Couldn't load your progress right now
        </Typography>
      ) : (
        <PlaceholderFade active={isPlaceholderData}>
          {/* Gapped while loading, so the skeletons read as separate rows */}
          <Stack spacing={isLoading ? 1 : 0}>
            {isLoading
              ? Array.from({ length: 3 }, (_, i) => (
                  <Skeleton key={i} variant="rectangular" height={56} />
                ))
              : rows.map((row) => (
                  <AchievementRow
                    key={row.key}
                    row={{
                      ...row,
                      isNew: row.isNew || newThisVisit.has(row.key),
                    }}
                    // Not while showing the previous pill's rows
                    onSeen={
                      row.isNew && !isPlaceholderData
                        ? () => handleSeen(row.key)
                        : undefined
                    }
                  />
                ))}
          </Stack>
        </PlaceholderFade>
      )}
    </Box>
  );
}
